import { Entregable } from "@/models/Entregable.model";
import { DefaultContainer } from "@base/components/layout/containers/DefaultContainer";
import { TablaQuery } from "@base/components/tables/TablaQuery";
import useModelColumns from "@base/hooks/useModelColumns/useModelColumns";
import { PaginaProvider, usePagina } from "@base/hooks/usePagina/usePagina";
import { Icon } from "@iconify/react/dist/iconify.js";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Grid,
  Modal,
  Progress,
  Row,
  Spin,
  Tag,
} from "antd";
import { useMemo, useState } from "react";
import parse from "html-react-parser";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import useHttp from "@base/hooks/useHttp/useHttp";
import { useAuth } from "@/hooks/useAuth/useAuth";
import { Empresa } from "@/models/Empresa.model";
import { TipoDocumento } from "@/models/TipoDocumento.model";
import { Expediente } from "@/models/Expediente.model";
import {
  CampoFaltante,
  calcularLlenadoPerfil,
} from "@/utils/evaluarEmpresaPerfil";
import ProgresoAvatar from "./entregas/-components/ProgresoAvatar";

export const Route = createFileRoute("/(auth)/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { requestParams, setRequestParams } = usePagina();
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const http = useHttp();
  const [dashboard, setDashBoard] = useState<Record<string, any> | undefined>(
    undefined,
  );
  const [modalFaltantesOpen, setModalFaltantesOpen] = useState(false);

  const screens = Grid.useBreakpoint();
  const isDesktop = !!screens.lg; // true si el ancho es >= lg (992px)

  const columns = useModelColumns({
    columns: Entregable.COLUMNS,
  });

  const entregablesParams = {
    ...requestParams,
    limite: 5,
    expand: "empresa,totalAdjuntos,totalEntregables,proyecto",
    ordenar: "creado-desc",
  };

  // Solo se consulta cuando se muestran tarjetas (menor a lg)
  const qEntregablesCards = useQuery({
    queryKey: [Entregable.ENDPOINTS.DEFAULT, "cards", entregablesParams, http],
    queryFn: () =>
      http.get({
        endpoint: Entregable.ENDPOINTS.DEFAULT,
        params: entregablesParams,
      }),
    enabled: !isDesktop,
  });

  const entregablesCards: any[] = qEntregablesCards.data?.resultado ?? [];

  useQuery({
    queryKey: [Entregable.ENDPOINTS.DASHBOARD, http, setDashBoard],
    queryFn: () =>
      http.get({
        endpoint: Entregable.ENDPOINTS.DASHBOARD,
        onSuccess: (resp) => {
          if (resp?.detalle) {
            setDashBoard(resp?.detalle);
          }
        },
      }),
  });

  // Consulta Empresa para evaluación delPerfil
  const qEmpresa = useQuery({
    queryKey: [Empresa.ENDPOINTS.DEFAULT, usuario?.id, http],
    queryFn: () =>
      http.get({
        endpoint: Empresa.ENDPOINTS.DEFAULT,
        params: {
          expand: "grupos,empresaGrupos,empresaGrupos.grupo",
        },
      }),
  });

  // Consulta TipoDocumento y Expedientes para evaluación del Expediente
  const qDocumentos = useQuery({
    queryKey: [TipoDocumento.ENDPOINTS.DEFAULT, http],
    queryFn: () =>
      http.get({
        endpoint: TipoDocumento.ENDPOINTS.DEFAULT,
        params: { tipo: "" },
      }),
  });

  const qExpedientesAll = useQuery({
    queryKey: [Expediente.ENDPOINTS.DEFAULT, "all", http],
    queryFn: () =>
      http.get({
        endpoint: Expediente.ENDPOINTS.DEFAULT,
        params: { expand: "media" },
      }),
  });

  const rawEmpresa =
    qEmpresa.data?.resultado ?? qEmpresa.data?.detalle ?? qEmpresa.data;
  const empresaData = Array.isArray(rawEmpresa) ? rawEmpresa[0] : rawEmpresa;
  const currentData = {
    ...empresaData,
    correoUsuario: empresaData?.correoUsuario || usuario?.correoUsuario,
  };
  const {
    porcentaje: porcentajePerfil,
    completados: camposCompletados,
    totales: totalCampos,
    faltantes,
  } = calcularLlenadoPerfil(currentData);

  const documentos: TipoDocumento[] = qDocumentos.data?.resultado ?? [];
  const expedientesAll: Expediente[] = qExpedientesAll.data?.resultado ?? [];
  const totalTiposExp = documentos.length;
  const atendidosCountExp = documentos.filter((doc) =>
    expedientesAll.some((exp) => String(exp.idTipo) === String(doc.id)),
  ).length;
  const porcentajeExpediente =
    totalTiposExp > 0
      ? Math.round((atendidosCountExp / totalTiposExp) * 100)
      : 0;
  const isExpedienteCompleto =
    totalTiposExp > 0 && atendidosCountExp >= totalTiposExp;
  const isPerfilCompleto = porcentajePerfil === 100;

  const getAvatarByProgress = (completed: number, total: number) => {
    const percentage = total === 0 ? 0 : (completed / total) * 100;

    const color =
      percentage === 100
        ? "#22c55e"
        : percentage >= 75
          ? "#15803d"
          : percentage >= 50
            ? "#f97316"
            : percentage >= 33
              ? "#eab308"
              : "#ef4444";

    const isRed = percentage < 33 && total > 0;

    return (
      <div style={{ position: "relative", display: "inline-flex" }}>
        {isRed && (
          <span
            style={{
              position: "absolute",
              display: "inline-flex",
              width: "100%",
              height: "100%",
              borderRadius: "9999px",
              backgroundColor: "#fb2c36",
              opacity: 0.75,
              animation: "progress-ping 1s cubic-bezier(0, 0, 0.2, 1) infinite",
            }}
          />
        )}
        <Avatar
          size="small"
          style={{ backgroundColor: color, color: "#ffffff" }}
        >
          {completed}/{total}
        </Avatar>
      </div>
    );
  };

  const _columns = useMemo(() => {
    const cols = columns.map((c) => {
      if (c.key === "folio") {
        return {
          ...c,
          render: (v: any, r: any) => (
            <Link to="/entregas/detalle" search={{ id: r?.id }}>
              {r?.folio || v || "-"}
            </Link>
          ),
        };
      }
      return c;
    });

    cols.push({
      title: "Descripción",
      dataIndex: "proyecto.descripcion",
      key: "proyecto.descripcion",
      render: (_: any, r: any) => (
        <Link to="/entregas/detalle" search={{ id: r?.id }}>
          {parse(`${r?.proyecto?.descripcion}`)}
        </Link>
      ),
    });

    cols.push({
      title: "Fecha de Inicio",
      dataIndex: "proyecto.inicio",
      key: "proyecto.inicio",
      render: (_: any, r: any) => (
        <Link to="/entregas/detalle" search={{ id: r?.id }}>
          {r?.proyecto?.inicio
            ? dayjs(r?.proyecto?.inicio).format("DD/MM/YYYY HH:mm")
            : "-"}
        </Link>
      ),
    });
    cols.push({
      title: "Fecha de Entrega",
      dataIndex: "proyecto.fin",
      key: "proyecto.fin",
      render: (_: any, r: any) => (
        <Link to="/entregas/detalle" search={{ id: r?.id }}>
          {r?.proyecto?.fin
            ? dayjs(r?.proyecto?.fin).format("DD/MM/YYYY HH:mm")
            : "-"}
        </Link>
      ),
    });

    cols.push({
      title: "Adjuntos",
      dataIndex: "totalAdjuntos",
      key: "totalAdjuntos",
      align: "center",
      render: (_: any, r: any) => (
        <Link to="/entregas/detalle" search={{ id: r?.id }}>
          <div className="flex flex-row justify-center">
            <Avatar size={"small"} className="bg-neutral-950">
              {r?.totalAdjuntos}
            </Avatar>
          </div>
        </Link>
      ),
    });

    cols.push({
      title: "Entregas",
      dataIndex: "totalEntregables",
      key: "totalEntregables",
      align: "center",
      render: (_: any, r: any) => (
        <Link to="/entregas/detalle" search={{ id: r?.id }}>
          <ProgresoAvatar
            completed={r?.totalEntregables?.pendientes ?? 0}
            total={r?.totalEntregables?.total ?? 0}
          />
        </Link>
      ),
    });

    return cols;
  }, [columns]);

  return (
    <PaginaProvider titulo="Inicio">
      <DefaultContainer className="h-full">
        <Row gutter={[10, 14]}>
          <Col span={24}>
            <p className="font-semibold text-5xl">Tablero</p>
          </Col>

          <Col span={24}>
            {isDesktop ? (
              <Card classNames={{ body: "p-4" }}>
                <div className="w-full">
                  <TablaQuery
                    columns={_columns}
                    queryProps={{
                      endpoint: Entregable.ENDPOINTS.DEFAULT,
                      extraParams: entregablesParams,
                    }}
                    setRequestParams={setRequestParams}
                  />
                </div>
              </Card>
            ) : (
              <Spin spinning={qEntregablesCards.isLoading}>
                {entregablesCards.length === 0 &&
                !qEntregablesCards.isLoading ? (
                  <Empty description="Sin entregables" />
                ) : (
                  <Row gutter={[10, 10]}>
                    {entregablesCards.map((r: any) => (
                      <Col key={r?.id} xs={24} md={12}>
                        <Card
                          hoverable
                          size="small"
                          className="h-full"
                          onClick={() =>
                            navigate({
                              to: "/entregas/detalle",
                              search: { id: r?.id },
                            })
                          }
                          title={
                            <Link
                              to="/entregas/detalle"
                              search={{ id: r?.id }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {r?.folio || "-"}
                            </Link>
                          }
                          extra={
                            <ProgresoAvatar
                              completed={r?.totalEntregables?.pendientes ?? 0}
                              total={r?.totalEntregables?.total ?? 0}
                            />
                          }
                        >
                          <div className="flex flex-col gap-2 text-sm">
                            <div>
                              <span className="block text-xs text-gray-500 font-medium">
                                Descripción
                              </span>
                              <div>
                                {r?.proyecto?.descripcion
                                  ? parse(`${r.proyecto.descripcion}`)
                                  : "-"}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <span className="block text-xs text-gray-500 font-medium">
                                  Fecha de Inicio
                                </span>
                                <span>
                                  {r?.proyecto?.inicio
                                    ? dayjs(r.proyecto.inicio).format(
                                        "DD/MM/YYYY HH:mm",
                                      )
                                    : "-"}
                                </span>
                              </div>
                              <div>
                                <span className="block text-xs text-gray-500 font-medium">
                                  Fecha de Entrega
                                </span>
                                <span>
                                  {r?.proyecto?.fin
                                    ? dayjs(r.proyecto.fin).format(
                                        "DD/MM/YYYY HH:mm",
                                      )
                                    : "-"}
                                </span>
                              </div>
                            </div>

                            <Divider className="my-1" />

                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500 font-medium">
                                Adjuntos
                              </span>
                              <Avatar size="small" className="bg-neutral-950">
                                {r?.totalAdjuntos ?? 0}
                              </Avatar>
                            </div>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </Spin>
            )}
          </Col>

          {/* Alerta de Llenado de Perfil */}
          {!isPerfilCompleto && (
            <Col span={24}>
              <Alert
                type="warning"
                showIcon
                icon={
                  <Icon
                    icon="lucide:user-x"
                    className="text-xl text-amber-600"
                  />
                }
                className="border-amber-300 bg-amber-50"
                message={
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="font-bold text-base text-amber-900 block">
                        Finaliza el llenado de tu perfil
                      </span>
                      <span className="text-sm text-amber-800">
                        Completa la información requerida de tu perfil de
                        proveedor ({porcentajePerfil}% completado). Faltan{" "}
                        {faltantes.length} campos.
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-amber-900 text-sm">
                          {camposCompletados}/{totalCampos}
                        </span>
                        <Progress
                          percent={porcentajePerfil}
                          size="small"
                          style={{ width: 100 }}
                          strokeColor="#fa8c16"
                        />
                      </div>
                      <Button
                        size="small"
                        type="default"
                        className="border-amber-400 text-amber-900 font-medium hover:bg-amber-100"
                        onClick={() => setModalFaltantesOpen(true)}
                      >
                        Ver faltantes
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        className="bg-amber-600 hover:bg-amber-700 font-medium"
                        onClick={() => navigate({ to: "/perfil" })}
                      >
                        Ir a Mi Perfil
                      </Button>
                    </div>
                  </div>
                }
              />
            </Col>
          )}

          {/* Alerta de Llenado de Expediente */}
          {!isExpedienteCompleto && (
            <Col span={24}>
              <Alert
                type="error"
                showIcon
                icon={
                  <Icon
                    icon="lucide:file-warning"
                    className="text-xl text-red-600"
                  />
                }
                className="border-red-300 bg-red-50"
                message={
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="font-bold text-base text-red-900 block">
                        Finaliza el llenado de tu expediente
                      </span>
                      <span className="text-sm text-red-800">
                        Adjunta la documentación requerida en tu expediente
                        digital ({porcentajeExpediente}% completado).
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-red-900 text-sm">
                          {atendidosCountExp}/{totalTiposExp}
                        </span>
                        <Progress
                          percent={porcentajeExpediente}
                          size="small"
                          style={{ width: 100 }}
                          strokeColor="#ff4d4f"
                        />
                      </div>
                      <Button
                        size="small"
                        type="primary"
                        danger
                        className="font-medium"
                        onClick={() => navigate({ to: "/expediente" })}
                      >
                        Ir a Expediente
                      </Button>
                    </div>
                  </div>
                }
              />
            </Col>
          )}
        </Row>

        {/* Modal de Campos Faltantes del Perfil */}
        <Modal
          title={null}
          open={modalFaltantesOpen}
          onCancel={() => setModalFaltantesOpen(false)}
          footer={[
            <Button key="close" onClick={() => setModalFaltantesOpen(false)}>
              Cerrar
            </Button>,
            <Button
              key="irPerfil"
              type="primary"
              onClick={() => {
                setModalFaltantesOpen(false);
                navigate({ to: "/perfil" });
              }}
            >
              Ir a Mi Perfil
            </Button>,
          ]}
          width={500}
          centered
          destroyOnClose
        >
          <div className="flex items-start gap-3 mb-4 mt-2">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-600 shrink-0">
              <Icon icon="lucide:alert-triangle" fontSize={22} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 m-0">
                Perfil Incompleto ({porcentajePerfil}%)
              </h3>
              <p className="text-xs text-gray-500 font-medium m-0 mt-0.5">
                Los siguientes campos deben ser completados en tu perfil de
                proveedor.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center justify-between text-xs">
            <span className="font-semibold text-amber-900 flex items-center gap-1.5">
              <Icon icon="lucide:info" className="text-amber-600 text-sm" />
              Campos faltantes:
            </span>
            <Tag color="warning" className="font-bold border-amber-300 m-0">
              {faltantes.length} faltante{faltantes.length === 1 ? "" : "s"}
            </Tag>
          </div>

          <div className="max-h-[260px] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {faltantes.map((f: CampoFaltante, idx: number) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 p-2 rounded-md bg-gray-50 border border-gray-200 text-xs font-medium text-gray-700"
                >
                  <Icon
                    icon="lucide:x-circle"
                    className="text-red-500 text-sm shrink-0"
                  />
                  <span className="truncate">{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      </DefaultContainer>
    </PaginaProvider>
  );
}
